import { useEffect, useMemo, useRef, useState } from "react";
import {
  Alert,
  Box,
  CircularProgress,
  FormControl,
  IconButton,
  InputAdornment,
  InputLabel,
  MenuItem,
  Select,
  TextField,
  Typography,
} from "@mui/material";
import VisibilityOutlinedIcon from "@mui/icons-material/VisibilityOutlined";
import VisibilityOffOutlinedIcon from "@mui/icons-material/VisibilityOffOutlined";
import { useTranslation } from "react-i18next";
import { toast } from "react-toastify";
import axios from "axios";
import CustomButton from "../../../components/buttons/CustomButton";
import { useAuthRoles } from "../../../hooks/useAuthRoles";
import { useWorkerApiHelper } from "../../../hooks/useWorkerApiHelper";
import { useGetParticipants } from "../../../hooks/participants/useGetParticipants";
import { getParticipantDisplayName } from "../../../utils/participants";
import { buildServiceUrl } from "../../../utils/urlBuilder";
import "../CreateUser.css";

const AVAILABLE_ROLES = ["data_provider", "data_consumer"];

const EMPTY_FORM = {
  username: "",
  firstName: "",
  lastName: "",
  email: "",
  organization: "",
  participantId: "",
  password: "",
  confirmPassword: "",
  roles: [],
};

const EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
const PASSWORD_REGEX =
  /^(?=.*[a-z])(?=.*[A-Z])(?=(?:.*\d){2,})(?=(?:.*[^A-Za-z0-9]){2,}).{15,}$/;

function normalize(value) {
  return String(value ?? "").trim();
}

export default function CreateUserForm({ onCancel, onSuccess }) {
  const { t, i18n } = useTranslation();
  const { token } = useAuthRoles();
  const { callApiWithWorker } = useWorkerApiHelper();
  const participantFieldRef = useRef(null);
  const [formData, setFormData] = useState(EMPTY_FORM);
  const [errors, setErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [participantMenuWidth, setParticipantMenuWidth] = useState(null);

  const { data: participantsResponse, isLoading: isParticipantsLoading } =
    useGetParticipants({
      callApiWithWorker,
      lang: i18n.language,
      pageIndex: 1,
      pageSize: 1000,
    });

  const roleOptions = useMemo(
    () =>
      AVAILABLE_ROLES.map((value) => ({
        value,
        label: t(`roles.${value}`, value),
      })),
    [t],
  );

  const participantOptions = useMemo(() => {
    const content = participantsResponse?.content ?? [];

    return content
      .map((participant) => {
        const participantId =
          participant?.dsbParticipantId ??
          participant?.identifier?.[0]?.notation ??
          participant?.id ??
          "";
        const participantName =
          getParticipantDisplayName(participant, i18n.language) ||
          participant?.legal_name ||
          participantId;

        return {
          value: participantId,
          label: `${participantName}`,
          displayLabel: participantName,
          organization: participantName,
        };
      })
      .filter((participant) => participant.value)
      .sort((a, b) => a.label.localeCompare(b.label));
  }, [i18n.language, participantsResponse]);

  const participantLookup = useMemo(
    () =>
      participantOptions.reduce((acc, participant) => {
        acc[participant.value] = participant;
        return acc;
      }, {}),
    [participantOptions],
  );

  useEffect(() => {
    const updateParticipantMenuWidth = () => {
      setParticipantMenuWidth(participantFieldRef.current?.offsetWidth ?? null);
    };

    updateParticipantMenuWidth();
    window.addEventListener("resize", updateParticipantMenuWidth);

    return () => {
      window.removeEventListener("resize", updateParticipantMenuWidth);
    };
  }, []);

  const setField = (field, value) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
    setErrors((prev) => {
      if (!prev[field]) return prev;
      const next = { ...prev };
      delete next[field];
      return next;
    });
  };

  const handleParticipantChange = (event) => {
    const participantId = event.target.value;
    const participant = participantLookup[participantId];

    setFormData((prev) => ({
      ...prev,
      participantId,
      organization: participant?.organization ?? participantId,
    }));
    setErrors((prev) => {
      const next = { ...prev };
      delete next.participantId;
      delete next.organization;
      return next;
    });
  };

  const validate = () => {
    const nextErrors = {};

    [
      "username",
      "firstName",
      "lastName",
      "email",
      "organization",
      "participantId",
      "password",
      "confirmPassword",
    ].forEach((field) => {
      if (!normalize(formData[field])) {
        nextErrors[field] = t("createuser.validation.required");
      }
    });

    if (formData.email && !EMAIL_REGEX.test(formData.email)) {
      nextErrors.email = t("createuser.validation.invalidEmail");
    }

    if (formData.password && !PASSWORD_REGEX.test(formData.password)) {
      nextErrors.password = t("createuser.validation.passwordFormat");
    }

    if (
      formData.password &&
      formData.confirmPassword &&
      formData.password !== formData.confirmPassword
    ) {
      nextErrors.confirmPassword = t("createuser.validation.passwordMatch");
    }

    if (!formData.roles.length) {
      nextErrors.roles = t("createuser.validation.rolesRequired");
    }

    setErrors(nextErrors);
    return Object.keys(nextErrors).length === 0;
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    if (!validate() || !token) return;

    setIsSubmitting(true);

    try {
      const payload = {
        username: normalize(formData.username),
        email: normalize(formData.email),
        firstName: normalize(formData.firstName),
        lastName: normalize(formData.lastName),
        organization: normalize(formData.organization),
        participantId: normalize(formData.participantId),
        password: formData.password,
        temporaryPassword: false,
        roles: formData.roles,
      };

      await axios.post(
        buildServiceUrl("connectorMaster", "keycloak/internal/users"),
        payload,
        {
          headers: { Authorization: `Bearer ${token}` },
        },
      );

      toast.success(t("createuser.success"));
      setFormData(EMPTY_FORM);
      setErrors({});
      await onSuccess?.();
    } catch (error) {
      if (error?.response?.status === 409) {
        toast.error(t("createuser.duplicateError"));
      } else {
        toast.error(t("createuser.error"));
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <form className="create-user-form" onSubmit={handleSubmit}>
      <Alert severity="info" className="create-user-alert">
        {t("createuser.helper")}
      </Alert>

      <div className="create-user-grid">
        <TextField
          label={t("createuser.fields.username")}
          value={formData.username}
          onChange={(e) => setField("username", e.target.value)}
          error={Boolean(errors.username)}
          helperText={errors.username || t("createuser.usernameHint")}
          fullWidth
        />
        <TextField
          label={t("usermanagement.email")}
          value={formData.email}
          onChange={(e) => setField("email", e.target.value)}
          error={Boolean(errors.email)}
          helperText={errors.email}
          fullWidth
        />
        <TextField
          label={t("usermanagement.name")}
          value={formData.firstName}
          onChange={(e) => setField("firstName", e.target.value)}
          error={Boolean(errors.firstName)}
          helperText={errors.firstName}
          fullWidth
        />
        <TextField
          label={t("register.surname")}
          value={formData.lastName}
          onChange={(e) => setField("lastName", e.target.value)}
          error={Boolean(errors.lastName)}
          helperText={errors.lastName}
          fullWidth
        />
        <FormControl
          ref={participantFieldRef}
          fullWidth
          error={Boolean(errors.participantId)}
        >
          <InputLabel id="create-user-participant-label">
            {t("createuser.fields.participant")}
          </InputLabel>
          <Select
            labelId="create-user-participant-label"
            value={formData.participantId}
            label={t("createuser.fields.participant")}
            onChange={handleParticipantChange}
            disabled={isParticipantsLoading}
            renderValue={(selected) =>
              participantLookup[selected]?.displayLabel ??
              participantLookup[selected]?.organization ??
              selected
            }
            MenuProps={{
              PaperProps: {
                sx: {
                  width: participantMenuWidth ?? undefined,
                  maxWidth: participantMenuWidth ?? undefined,
                  maxHeight: 48 * 6,
                },
              },
            }}
          >
            {participantOptions.map((participant) => (
              <MenuItem
                key={participant.value}
                value={participant.value}
                sx={{
                  whiteSpace: "normal",
                  overflowWrap: "anywhere",
                }}
              >
                {participant.label}
              </MenuItem>
            ))}
          </Select>
          <Typography
            variant="caption"
            color={errors.participantId ? "error" : "text.secondary"}
            sx={{ mt: 1, ml: 1.75 }}
          >
            {errors.participantId ||
              (isParticipantsLoading ? (
                <span className="create-user-inline-loading">
                  <CircularProgress size={14} />
                  {t("createuser.loadingParticipants")}
                </span>
              ) : (
                t("createuser.participantHint")
              ))}
          </Typography>
        </FormControl>
        <TextField
          label={t("register.organization")}
          value={formData.organization}
          error={Boolean(errors.organization)}
          helperText={errors.organization}
          fullWidth
          InputProps={{ readOnly: true }}
        />
        <FormControl fullWidth error={Boolean(errors.roles)}>
          <InputLabel id="create-user-roles-label">
            {t("usermanagement.role")}
          </InputLabel>
          <Select
            labelId="create-user-roles-label"
            value={formData.roles[0] ?? ""}
            label={t("usermanagement.role")}
            onChange={(e) => setField("roles", [e.target.value])}
            renderValue={(selected) =>
              roleOptions.find((role) => role.value === selected)?.label ||
              selected
            }
          >
            {roleOptions.map((role) => (
              <MenuItem key={role.value} value={role.value}>
                {role.label}
              </MenuItem>
            ))}
          </Select>
          {errors.roles ? (
            <Typography
              variant="caption"
              color="error"
              sx={{ mt: 1, ml: 1.75 }}
            >
              {errors.roles}
            </Typography>
          ) : null}
        </FormControl>
        <TextField
          label={t("createuser.fields.password")}
          type={showPassword ? "text" : "password"}
          value={formData.password}
          onChange={(e) => setField("password", e.target.value)}
          error={Boolean(errors.password)}
          helperText={errors.password || t("createuser.passwordHint")}
          fullWidth
          InputProps={{
            endAdornment: (
              <InputAdornment position="end">
                <IconButton
                  aria-label={showPassword ? "Hide password" : "Show password"}
                  edge="end"
                  onClick={() => setShowPassword((prev) => !prev)}
                >
                  {showPassword ? (
                    <VisibilityOffOutlinedIcon />
                  ) : (
                    <VisibilityOutlinedIcon />
                  )}
                </IconButton>
              </InputAdornment>
            ),
          }}
        />
        <TextField
          label={t("createuser.fields.confirmPassword")}
          type={showConfirmPassword ? "text" : "password"}
          value={formData.confirmPassword}
          onChange={(e) => setField("confirmPassword", e.target.value)}
          error={Boolean(errors.confirmPassword)}
          helperText={errors.confirmPassword}
          fullWidth
          InputProps={{
            endAdornment: (
              <InputAdornment position="end">
                <IconButton
                  aria-label={
                    showConfirmPassword
                      ? "Hide confirm password"
                      : "Show confirm password"
                  }
                  edge="end"
                  onClick={() => setShowConfirmPassword((prev) => !prev)}
                >
                  {showConfirmPassword ? (
                    <VisibilityOffOutlinedIcon />
                  ) : (
                    <VisibilityOutlinedIcon />
                  )}
                </IconButton>
              </InputAdornment>
            ),
          }}
        />
      </div>

      <Box className="create-user-actions">
        <CustomButton
          type="button"
          variant="outlined"
          sx={{
            color: "var(--main-color)",
            borderColor: "var(--main-color)",
            backgroundColor: "#ffffff",
            "&:hover": {
              backgroundColor: "#ffffff",
              borderColor: "var(--main-color)",
            },
          }}
          onClick={onCancel}
          disabled={isSubmitting}
        >
          {t("profilePage.cancel")}
        </CustomButton>
        <CustomButton type="submit" disabled={isSubmitting || !token}>
          {isSubmitting
            ? t("createuser.creating")
            : t("usermanagement.createUser")}
        </CustomButton>
      </Box>
    </form>
  );
}
